// Shared evaluation logic for the `locked_design_styles` feature flag.
// Mirrored (kept simple) in `supabase/functions/transform-room/index.ts`.

export interface LockedStylesFlag {
  enabled_global: boolean;
  enabled_for_admins: boolean;
  enabled_user_ids: string[] | null;
  min_enforce_android_version: string | null;
  min_enforce_ios_version: string | null;
  admin_bypass?: boolean | null;
}

export interface EvalInput {
  flag: LockedStylesFlag | null;
  userId: string | null;
  userTier: string | null; // 'free' | 'pro' | ...
  isAdmin: boolean;
  platform: "web" | "ios" | "android";
  appVersion: string | null; // null on web
}

export interface EvalResult {
  enforced: boolean;
  reason:
    | "no-flag"
    | "paid-user"
    | "admin-bypass"
    | "version-gate"
    | "global"
    | "admins-only"
    | "beta-user"
    | "not-targeted";
}

// Compare two dotted version strings (e.g. "1.2.10" vs "1.2.3").
// Returns -1, 0, or 1. Missing parts are treated as 0.
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const av = pa[i] ?? 0;
    const bv = pb[i] ?? 0;
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  return 0;
};

export const evaluateLockedStyles = ({
  flag,
  userId,
  userTier,
  isAdmin,
  platform,
  appVersion,
}: EvalInput): EvalResult => {
  if (!flag) return { enforced: false, reason: "no-flag" };

  // 1. Paid users never see locks.
  if (userTier && userTier !== "free") {
    return { enforced: false, reason: "paid-user" };
  }

  // 1b. Admin bypass — admins get every style unless explicitly disabled.
  if (isAdmin && flag.admin_bypass !== false) {
    return { enforced: false, reason: "admin-bypass" };
  }


  // 2. Mobile version gate — protect users on older binaries that lack the lock UI.
  if (platform === "android" || platform === "ios") {
    const gate =
      platform === "android"
        ? flag.min_enforce_android_version
        : flag.min_enforce_ios_version;
    if (gate && appVersion && compareVersions(appVersion, gate) < 0) {
      return { enforced: false, reason: "version-gate" };
    }
    // If we don't know the version on a native build, be safe → don't enforce.
    if (gate && !appVersion) {
      return { enforced: false, reason: "version-gate" };
    }
  }

  // 3. Audience checks.
  if (flag.enabled_global) return { enforced: true, reason: "global" };
  if (flag.enabled_for_admins && isAdmin) {
    return { enforced: true, reason: "admins-only" };
  }
  if (userId && flag.enabled_user_ids && flag.enabled_user_ids.includes(userId)) {
    return { enforced: true, reason: "beta-user" };
  }

  return { enforced: false, reason: "not-targeted" };
};
